import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { FolderKanban, Filter, Search, Clock, AlertTriangle, CheckCircle2, ChevronDown } from 'lucide-react';

const STAGE_COLORS = {
  storyboard: '#8b5cf6', layout: '#06b6d4', animation: '#3b82f6',
  lighting: '#f59e0b', compositing: '#10b981', final: '#ec4899'
};
const STATUS_ORDER = ['blocked', 'not_started', 'in_progress', 'review', 'completed'];

export default function TasksPage() {
  const { api, user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filterProject, setFilterProject] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [search, setSearch] = useState('');
  const [editingTask, setEditingTask] = useState(null);

  useEffect(() => {
    Promise.all([api.get('/tasks'), api.get('/projects')]).then(([t, p]) => {
      setTasks(t.data);
      setProjects(p.data);
    });
  }, [api]);

  const updateTask = async (taskId, updates) => {
    const res = await api.put(`/tasks/${taskId}`, updates);
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...res.data } : t));
    setEditingTask(null);
  };

  const filtered = tasks.filter(t => {
    if (filterProject !== 'all' && t.project_id !== filterProject) return false;
    if (filterStatus !== 'all' && t.status !== filterStatus) return false;
    if (filterType !== 'all' && t.task_type !== filterType) return false;
    if (search && !t.name.toLowerCase().includes(search.toLowerCase()) && !(t.assignee_name || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const groupByStatus = STATUS_ORDER.reduce((acc, s) => {
    acc[s] = filtered.filter(t => t.status === s);
    return acc;
  }, {});

  return (
    <div className="space-y-5" data-testid="tasks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Task Manager</h1>
          <p className="text-xs text-zinc-500 mt-1">{filtered.length} tasks | {tasks.filter(t => t.status === 'in_progress').length} in progress</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
          <input data-testid="task-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tasks or assignee..." className="w-full h-8 pl-9 pr-3 bg-zinc-900 border border-zinc-800 rounded-sm text-xs text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-blue-500" />
        </div>
        <select data-testid="filter-project" value={filterProject} onChange={e => setFilterProject(e.target.value)} className="h-8 bg-zinc-900 border border-zinc-800 rounded-sm text-xs text-zinc-300 px-2 focus:outline-none focus:ring-1 focus:ring-blue-500">
          <option value="all">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select data-testid="filter-status" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="h-8 bg-zinc-900 border border-zinc-800 rounded-sm text-xs text-zinc-300 px-2 focus:outline-none focus:ring-1 focus:ring-blue-500">
          <option value="all">All Status</option>
          {STATUS_ORDER.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        <select data-testid="filter-type" value={filterType} onChange={e => setFilterType(e.target.value)} className="h-8 bg-zinc-900 border border-zinc-800 rounded-sm text-xs text-zinc-300 px-2 focus:outline-none focus:ring-1 focus:ring-blue-500">
          <option value="all">All Types</option>
          {Object.keys(STAGE_COLORS).map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {/* Kanban Board */}
      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-3">
        {STATUS_ORDER.map(status => {
          const statusTasks = groupByStatus[status] || [];
          const statusColor = status === 'completed' ? '#10b981' : status === 'in_progress' ? '#3b82f6' : status === 'blocked' ? '#ef4444' : status === 'review' ? '#8b5cf6' : '#71717a';
          return (
            <div key={status} className="bg-zinc-900/50 border border-zinc-800/50 rounded-sm" data-testid={`kanban-column-${status}`}>
              <div className="px-3 py-2.5 border-b border-zinc-800/50 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: statusColor }} />
                <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">{status.replace('_', ' ')}</span>
                <span className="ml-auto text-[10px] font-mono text-zinc-600">{statusTasks.length}</span>
              </div>
              <div className="p-2 space-y-1.5 max-h-[55vh] overflow-y-auto">
                {statusTasks.map(task => (
                  <div key={task.id} data-testid={`kanban-task-${task.id}`}
                    className="bg-zinc-900 border border-zinc-800 rounded-sm p-2.5 hover:border-zinc-700 transition-colors cursor-pointer group"
                    onClick={() => setEditingTask(task)}>
                    <p className="text-[11px] text-zinc-200 leading-tight">{task.name}</p>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <span className="text-[9px] font-mono px-1 py-0.5 rounded" style={{ backgroundColor: `${STAGE_COLORS[task.task_type]}20`, color: STAGE_COLORS[task.task_type] }}>{task.task_type}</span>
                      {task.is_overdue && <AlertTriangle className="w-3 h-3 text-red-400" />}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[9px] text-zinc-600">{task.assignee_name || 'Unassigned'}</span>
                      <span className="text-[9px] font-mono text-zinc-600">{task.progress}%</span>
                    </div>
                    {task.progress > 0 && task.progress < 100 && (
                      <div className="w-full h-1 bg-zinc-800 rounded-full mt-1 overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full" style={{ width: `${task.progress}%` }} />
                      </div>
                    )}
                    {/* Phase 3: launch Blender on the artist's PC with the task's .blend file.
                        Only shown when the task has a known blend_path. */}
                    {task.blend_path && (
                      <div className="mt-2 -mx-0.5" onClick={(e) => e.stopPropagation()}>
                        <OpenInBlenderButton
                          blendPath={task.blend_path}
                          taskId={task.id}
                          shotCode={task.shot_code || ''}
                          myTask={task.task_code || task.discipline_code || ''}
                          size="sm"
                          label="Open in Blender"
                        />
                      </div>
                    )}
                  </div>
                ))}
                {statusTasks.length === 0 && <p className="text-[10px] text-zinc-700 text-center py-4">No tasks</p>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Task Edit Modal */}
      {editingTask && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="task-edit-modal">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-[32rem]">
            <h3 className="text-sm font-heading font-semibold text-white mb-1">{editingTask.name}</h3>
            <p className="text-[10px] text-zinc-500 mb-4">{editingTask.task_type} | Assigned to: {editingTask.assignee_name}</p>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-[10px] text-zinc-500 uppercase">Status</label>
                <select value={editingTask.status} onChange={e => setEditingTask({...editingTask, status: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-zinc-300 px-2 focus:outline-none">
                  {STATUS_ORDER.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 uppercase">Progress</label>
                <input type="number" min="0" max="100" value={editingTask.progress} onChange={e => setEditingTask({...editingTask, progress: parseInt(e.target.value) || 0})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-zinc-300 px-2 focus:outline-none" />
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 uppercase">Priority (1-5)</label>
                <input type="number" min="1" max="5" value={editingTask.priority} onChange={e => setEditingTask({...editingTask, priority: parseInt(e.target.value) || 3})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-zinc-300 px-2 focus:outline-none" />
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 uppercase">Rate ({editingTask.rate_type})</label>
                <p className="text-sm font-mono text-emerald-400 mt-1">{editingTask.rate_amount?.toLocaleString()}</p>
              </div>
            </div>

            <div className="mb-4">
              <label className="text-[10px] text-zinc-500 uppercase">Notes</label>
              <textarea value={editingTask.notes || ''} onChange={e => setEditingTask({...editingTask, notes: e.target.value})} rows={2} className="w-full bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-zinc-300 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>

            <div className="flex gap-2">
              <button data-testid="task-save-btn" onClick={() => updateTask(editingTask.id, { status: editingTask.status, progress: editingTask.progress, priority: editingTask.priority, notes: editingTask.notes })} className="flex-1 h-8 bg-blue-600 hover:bg-blue-500 text-white text-xs rounded-sm">Save Changes</button>
              <button onClick={() => setEditingTask(null)} className="flex-1 h-8 bg-zinc-800 text-zinc-400 text-xs rounded-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
